"use client";

import React from "react";
import { Check, Star } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const tiers = [{
  name: "General Admission",
  price: "$175",
  description: "Immerse yourself in the vibrant flavors and rhythms of Sabor.",
  features: ["Unlimited culinary tastings", "Open bar with signature cocktails", "Live Latin music performances", "Access to Main Garden Pavilion", "Interactive art installations"],
  cta: "Get Tickets",
  href: "/tickets",
  featured: false
}, {
  name: "Ultra Lounge",
  price: "$750",
  description: "The pinnacle of luxury. An all-inclusive, red-carpet experience.",
  features: ["All VIP benefits included", "Private Ultra Lounge access", "Dedicated concierge service", "Premium champagne & caviar bar", "Reserved seating at main stage", "Exclusive after-party entry", "Valet parking included"],
  cta: "Request Access",
  href: "/tickets/ultra",
  featured: true,
  badge: "Most Exclusive"
}, {
  name: "VIP Experience",
  price: "$350",
  description: "Elevate your evening with early access and exclusive comforts.",
  features: ["1-hour early entry", "Exclusive VIP Lounge access", "Premium top-shelf spirits", "Private chef tasting stations", "Fast-track festival entry", "Comfortable lounge seating"],
  cta: "Get VIP",
  href: "/tickets/vip",
  featured: false
}];
export default function CuratedExperienceTiers() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="curated-experience-tiers" ref={ref} className="dark bg-background text-foreground py-24 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-[-10%] left-[-5%] w-[500px] h-[500px] bg-secondary/5 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
          <h2 className={cn("text-4xl md:text-5xl font-serif font-bold text-primary tracking-tight transition-all duration-700 transform", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
            Curated Experience Tiers
          </h2>
          <p className={cn("text-lg text-muted-foreground font-sans transition-all duration-700 delay-100 transform", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
            Choose your level of immersion. From the vibrant energy of the
            gardens to the secluded luxury of the Ultra Lounge.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
          {tiers.map((tier, index) => {
          const isUltra = tier.featured;
          return <div key={tier.name} data-index={index} className={cn("transition-all duration-700 transform h-full", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")} style={{
            transitionDelay: `${index * 150}ms`
          }}>
                <Card className={cn("relative h-full flex flex-col border transition-all duration-500 group bg-card/50 backdrop-blur-sm", isUltra ? "border-primary shadow-[0_0_40px_-10px_var(--color-secondary)] lg:-mt-8 lg:mb-8 z-10 scale-100 lg:scale-105" : "border-primary/30 hover:border-primary hover:shadow-[0_0_20px_-5px_var(--color-primary)] hover:shadow-primary/10")}>
                  {isUltra && <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-20">
                      <Badge className="bg-secondary text-secondary-foreground hover:bg-secondary px-4 py-1 text-sm font-semibold shadow-[0_0_15px_var(--color-secondary)] border-none uppercase tracking-wider">
                        {tier.badge}
                      </Badge>
                    </div>}

                  <CardHeader className="text-center pb-8 pt-8">
                    <CardTitle className={cn("text-2xl font-serif font-bold tracking-wide mb-2", isUltra ? "text-primary" : "text-foreground")}>
                      {tier.name}
                    </CardTitle>
                    <CardDescription className="text-muted-foreground min-h-[3rem]">
                      {tier.description}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="flex-grow px-8">
                    <div className="flex justify-center items-baseline mb-8">
                      <span className="text-4xl font-bold text-foreground font-serif">
                        {tier.price}
                      </span>
                      <span className="text-muted-foreground ml-2 text-sm">
                        / person
                      </span>
                    </div>

                    <ul className="space-y-4">
                      {tier.features.map((feature, i) => <li key={i} data-index={i} className="flex items-start gap-3 text-sm text-muted-foreground group-hover:text-foreground transition-colors duration-300">
                          <Check className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                          <span className="font-sans">{feature}</span>
                        </li>)}
                    </ul>
                  </CardContent>

                  <CardFooter className="pt-8 pb-8 px-8">
                    <Button asChild className={cn("w-full font-semibold tracking-wide text-base py-6 transition-all duration-300", isUltra ? "bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_20px_-5px_var(--color-primary)]" : "bg-transparent border border-primary/50 text-primary hover:bg-primary hover:text-primary-foreground")}>
                      <Link to={tier.href}>{tier.cta}</Link>
                    </Button>
                  </CardFooter>

                  {}
                  <div className="absolute inset-0 rounded-xl border border-primary/0 group-hover:border-primary/20 transition-all duration-500 pointer-events-none" />
                </Card>
              </div>;
        })}
        </div>
      </div>
    </section>;
}